/**
 * Desk variant A, `hierarchy`: today's two parts, what was read and what
 * is left, with the read part ranked rather than logged. Records are grouped
 * by kind in order of what an examiner presses on first, each record once;
 * lookups that returned no record sit apart, failures first.
 *
 * @module components/apps/probe/desk/HierarchyDesk.logic
 */
import type { ProbeBriefing } from "@amoreg/contracts";

import {
  deskLookupsOf,
  type ProbeDeskFile,
  type ProbeDeskInput,
  type ProbeDeskReading,
} from "../ProbeDesk.logic";
import {
  byImportance,
  DESK_KINDS,
  deskChecksOf,
  deskRecordsOf,
  type DeskCheck,
  type DeskKind,
  type DeskRecord,
} from "./deskRecords";

export interface HierarchyKindGroup {
  readonly kind: DeskKind;
  /** Opened or found by a search: findings most severe first, the rest as first read. */
  readonly opened: ReadonlyArray<DeskRecord>;
  /** Seen only in a list, ranked the same way. */
  readonly listed: ReadonlyArray<DeskRecord>;
  /** The lists of this kind looked through, by subject: "the open findings". */
  readonly lists: ReadonlyArray<string>;
}

export interface HierarchyDesk {
  readonly reading: ReadonlyArray<ProbeDeskReading>;
  /** Kinds read, in `DESK_KINDS` order. */
  readonly groups: ReadonlyArray<HierarchyKindGroup>;
  /** Failures first, then what came back empty, each lookup once. */
  readonly checks: ReadonlyArray<DeskCheck>;
  /** Records read, each once: what the desk shows. Empty lookups and failures are not reads. */
  readonly readCount: number;
  readonly file: ProbeDeskFile | null;
}

export const hierarchyDeskOf = (
  input: ProbeDeskInput,
  briefing: ProbeBriefing | null,
): HierarchyDesk => {
  const lookups = deskLookupsOf(input, briefing);
  const records = deskRecordsOf(lookups.finished, lookups);
  const lists = new Map<DeskKind, Array<string>>();
  for (const lookup of lookups.finished) {
    if (lookup.kind !== "list" || lookup.holds === null || lookup.records.length === 0) continue;
    const subjects = lists.get(lookup.holds) ?? [];
    if (!subjects.includes(lookup.subject)) lists.set(lookup.holds, [...subjects, lookup.subject]);
  }
  const checks = deskChecksOf(lookups.finished);
  const groups = DESK_KINDS.flatMap((kind): ReadonlyArray<HierarchyKindGroup> => {
    const ofKind = records.filter((record) => record.kind === kind).toSorted(byImportance);
    if (ofKind.length === 0) return [];
    return [
      {
        kind,
        opened: ofKind.filter((record) => record.opened),
        listed: ofKind.filter((record) => !record.opened),
        lists: lists.get(kind) ?? [],
      },
    ];
  });
  return {
    reading: lookups.reading,
    groups,
    checks,
    readCount: records.length,
    file: lookups.file,
  };
};

/**
 * How many rows each kind shows when the desk has room for `budget`: every
 * kind its most important row first, then the rest in rank order (findings
 * by severity, then the next kind's), so what folds into "N more" is always
 * the least important. Kinds keep their order; `sizes` are their row counts.
 */
export const fitRows = (sizes: ReadonlyArray<number>, budget: number): ReadonlyArray<number> => {
  const shown = sizes.map((size) => Math.min(size, 1));
  let left = budget - shown.reduce((sum, count) => sum + count, 0);
  for (let kind = 0; kind < sizes.length && left > 0; kind++) {
    const more = Math.min(sizes[kind]! - shown[kind]!, left);
    shown[kind] = shown[kind]! + more;
    left -= more;
  }
  return shown;
};
